import { formatMoney } from '@theme/utilities';
import { CartUpdatedEvent } from '@theme/events';

const SIZE_KEYS = ['size', 'taille', 'pointure', 'fit'];
const COLOR_KEYS = ['color', 'colour', 'couleur', 'coloris', 'teinte'];
const TYPE_KEYS = ['type', 'category', 'catégorie', 'categorie', 'style', 'modèle', 'modele'];

class PackBuilder extends HTMLElement {
  connectedCallback() {
    this.#cacheDom();
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    this.#desktopQuery?.addEventListener('change', this.#syncFold);

    const configEl = this.querySelector('[data-ref="config"]');
    if (configEl) {
      try {
        this.#config = JSON.parse((configEl.value || configEl.textContent || '').trim());
      } catch {
        this.#config = { i18n: {}, gift: {}, tiers: [], minItems: 1, filterBy: 'type' };
      }
    }

    try {
      this.#initCards();
      this.#buildFilters();
      this.#restore();
      this.#syncFold();
      this.#render();
    } catch (error) {
      console.error('[pack-builder]', error);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
    this.#desktopQuery?.removeEventListener('change', this.#syncFold);
  }

  #config = {};
  #items = [];
  #gift = null;
  #filterValue = '';
  #lastTierQuantity = 0;
  #cards = [];
  #giftOptions = [];
  #recapList = null;
  #itemTemplate = null;
  #progressFill = null;
  #progressPhrase = null;
  #tiers = [];
  #cta = null;
  #empty = null;
  #giftPicker = null;
  #giftList = null;
  #saveEl = null;
  #compareEl = null;
  #totalEl = null;
  #countEl = null;
  #summaryTotal = null;
  #filtersRoot = null;
  #fold = null;
  #desktopQuery = null;

  #cacheDom() {
    this.#cards = [...this.querySelectorAll('[data-ref="product-card"]')];
    this.#giftOptions = [...this.querySelectorAll('[data-ref="gift-option"]')];
    this.#recapList = this.querySelector('[data-ref="recap-list"]');
    this.#itemTemplate = this.querySelector('[data-ref="item-template"]');
    this.#progressFill = this.querySelector('[data-ref="progress-fill"]');
    this.#progressPhrase = this.querySelector('[data-ref="progress-phrase"]');
    this.#tiers = [...this.querySelectorAll('[data-ref="tier"]')];
    this.#cta = this.querySelector('[data-ref="add-to-cart"]');
    this.#empty = this.querySelector('[data-ref="recap-empty"]');
    this.#giftPicker = this.querySelector('[data-ref="gift-picker"]');
    this.#giftList = this.querySelector('[data-ref="gift-list"]');
    this.#saveEl = this.querySelector('[data-ref="save"]');
    this.#compareEl = this.querySelector('[data-ref="compare"]');
    this.#totalEl = this.querySelector('[data-ref="total"]');
    this.#countEl = this.querySelector('[data-ref="count"]');
    this.#summaryTotal = this.querySelector('[data-ref="summary-total"]');
    this.#filtersRoot = this.querySelector('[data-ref="filters"]');
    this.#fold = this.querySelector('[data-ref="recap-fold"]');
    this.#desktopQuery = window.matchMedia('(min-width: 750px)');
  }

  #syncFold = () => {
    if (!this.#fold || !this.#desktopQuery) return;

    if (this.#desktopQuery.matches) {
      this.#fold.open = true;
      return;
    }

    this.#fold.open = false;
  };

  #onClick = (event) => {
    const target = event.target.closest('[data-action]');
    if (!target) return;

    const action = target.dataset.action;

    if (action === 'add') {
      event.preventDefault();
      this.#addFromCard(target.closest('[data-ref="product-card"]'));
      return;
    }

    if (action === 'remove') {
      event.preventDefault();
      this.#removeItem(target.dataset.variantId);
      return;
    }

    if (action === 'increase') {
      event.preventDefault();
      this.#changeQty(target.dataset.variantId, 1);
      return;
    }

    if (action === 'decrease') {
      event.preventDefault();
      this.#changeQty(target.dataset.variantId, -1);
      return;
    }

    if (action === 'filter') {
      event.preventDefault();
      this.#setFilter(target.dataset.value);
      return;
    }

    if (action === 'add-to-cart') {
      event.preventDefault();
      this.#addToCart();
      return;
    }

    if (action === 'select-gift') {
      event.preventDefault();
      this.#selectGift(target.closest('[data-ref="gift-option"]'));
    }
  };

  #onChange = (event) => {
    const card = event.target.closest('[data-ref="product-card"], [data-ref="gift-option"]');
    if (!card) return;

    this.#syncCard(card);

    if (card.matches('[data-ref="gift-option"]') && this.#gift) {
      this.#selectGift(card);
    }
  };

  #initCards() {
    for (const card of [...this.#cards, ...this.#giftOptions]) {
      this.#syncCard(card);
    }
  }

  #getProductData(card) {
    const raw = card.querySelector('[data-ref="product-json"]');
    if (!raw) return null;

    try {
      return JSON.parse((raw.value || raw.textContent || '').trim());
    } catch {
      return null;
    }
  }

  #isIgnoredOption(name) {
    return String(name || '')
      .trim()
      .toLowerCase() === 'pack';
  }

  #optionKind(name) {
    const key = String(name || '').toLowerCase();
    if (this.#isIgnoredOption(key)) return 'pack';
    if (SIZE_KEYS.some((token) => key.includes(token))) return 'size';
    if (COLOR_KEYS.some((token) => key.includes(token))) return 'color';
    if (TYPE_KEYS.some((token) => key.includes(token))) return 'type';
    return null;
  }

  #selectedOptions(card) {
    const selected = {};

    for (const input of card.querySelectorAll('input[type="radio"]:checked')) {
      if (!input.dataset.optionName) continue;
      selected[input.dataset.optionName] = input.value;
    }

    return selected;
  }

  #variantMatches(product, variant, selected) {
    const options = Array.isArray(variant.options) ? variant.options : [];

    return (product.options || []).every((option) => {
      if (this.#isIgnoredOption(option.name)) return true;
      const chosen = selected[option.name];
      if (!chosen) return true;
      return String(options[option.position - 1] ?? '') === String(chosen);
    });
  }

  #findVariant(product, selected) {
    if (!product?.variants?.length) return null;

    const matches = product.variants.filter((variant) => this.#variantMatches(product, variant, selected));
    const available = matches.filter((variant) => variant.available);
    const pool = available.length ? available : matches;
    const packIndex = (product.options || []).findIndex((option) => this.#isIgnoredOption(option.name));

    if (packIndex >= 0) {
      const withoutPack = pool.find((variant) => {
        const value = String(variant.options?.[packIndex] ?? '')
          .trim()
          .toLowerCase();
        return value !== 'pack';
      });
      if (withoutPack) return withoutPack;
    }

    return pool[0] || null;
  }

  #resolveVariant(card) {
    const product = this.#getProductData(card);
    if (!product) return null;

    const selected = this.#selectedOptions(card);
    const matched = this.#findVariant(product, selected);
    if (matched) return { product, variant: matched };

    const available = product.variants.find((item) => item.available);
    return available ? { product, variant: available } : { product, variant: product.variants[0] || null };
  }

  #syncCard(card) {
    const resolved = this.#resolveVariant(card);
    const variant = resolved?.variant;
    if (!variant) return;

    const product = resolved.product;
    card.dataset.variantId = String(variant.id);
    card.dataset.price = String(variant.price ?? card.dataset.price ?? 0);
    card.dataset.available = variant.available ? 'true' : 'false';

    const image = card.querySelector('.pack-builder__card-image');
    if (image && variant.featured_image) {
      image.src = variant.featured_image;
    }

    const price = card.querySelector('[data-ref="card-price"]');
    if (price) {
      price.textContent = formatMoney(variant.price);
    }

    const addButton = card.querySelector('[data-action="add"]');
    if (addButton) {
      addButton.disabled = !variant.available;
      const label = addButton.querySelector('[data-ref="add-label"]');
      if (label) {
        label.textContent = variant.available ? this.#config.i18n.addToPack : this.#config.i18n.soldOut;
      }
    }

    if (product) this.#markUnavailableOptions(card, product, this.#selectedOptions(card));
  }

  #markUnavailableOptions(card, product, selected) {
    for (const input of card.querySelectorAll('input[type="radio"]')) {
      const optionName = input.dataset.optionName;
      const trial = { ...selected, [optionName]: input.value };
      const match = this.#findVariant(product, trial);
      input.setAttribute('aria-disabled', match?.available ? 'false' : 'true');
    }
  }

  #buildFilters() {
    if (!this.#filtersRoot) return;

    const filterBy = this.#config.filterBy || 'type';
    const values = new Set();

    for (const card of this.#cards) {
      const product = this.#getProductData(card);
      if (!product) {
        if (filterBy === 'type' && card.dataset.productType) values.add(card.dataset.productType);
        continue;
      }

      if (filterBy === 'type') {
        if (product.type) values.add(product.type);
        this.#optionValues(product, 'type').forEach((value) => values.add(value));
        continue;
      }

      this.#optionValues(product, filterBy).forEach((value) => values.add(value));
    }

    this.#renderFilterGroup(filterBy, values);
    this.#filtersRoot.hidden = values.size < 2;
  }

  #optionValues(product, kind) {
    const values = [];

    for (const option of product.options || []) {
      if (this.#optionKind(option.name) !== kind) continue;
      for (const value of option.values || []) {
        if (value) values.push(String(value));
      }
    }

    return values;
  }

  #renderFilterGroup(key, values) {
    const group = this.#filtersRoot.querySelector(`[data-ref="filter-${key}"]`);
    if (!group) return;

    if (values.size < 2) {
      group.hidden = true;
      return;
    }

    group.hidden = false;
    const chips = group.querySelector('[data-ref="filter-chips"]');
    if (!chips) return;

    chips.replaceChildren();
    chips.append(this.#chipButton('', this.#config.i18n.all, true));

    for (const value of values) {
      chips.append(this.#chipButton(value, value, false));
    }
  }

  #chipButton(value, label, active) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `pack-builder__chip${active ? ' is-active' : ''}`;
    button.dataset.action = 'filter';
    button.dataset.value = value;
    button.textContent = label;
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
    return button;
  }

  #setFilter(value) {
    this.#filterValue = value || '';

    for (const chip of this.#filtersRoot.querySelectorAll('[data-action="filter"]')) {
      const active = chip.dataset.value === this.#filterValue;
      chip.classList.toggle('is-active', active);
      chip.setAttribute('aria-pressed', active ? 'true' : 'false');
    }

    this.#applyFilters();
  }

  #applyFilters() {
    let visible = 0;

    for (const card of this.#cards) {
      const product = this.#getProductData(card);
      const match = this.#cardMatches(card, product);
      card.hidden = !match;
      if (match) visible += 1;
    }

    const empty = this.querySelector('[data-ref="grid-empty"]');
    if (empty) empty.hidden = visible > 0;
  }

  #cardMatches(card, product) {
    if (!this.#filterValue) return true;

    const filterBy = this.#config.filterBy || 'type';

    if (filterBy === 'type') {
      const type = product?.type || card.dataset.productType || '';
      if (type === this.#filterValue) return true;
      return Boolean(product && this.#optionValues(product, 'type').includes(this.#filterValue));
    }

    if (!product) return true;
    return this.#optionValues(product, filterBy).includes(this.#filterValue);
  }

  #addFromCard(card) {
    if (!card) return;

    const resolved = this.#resolveVariant(card);
    const variant = resolved?.variant;
    const product = resolved?.product;
    const variantId = Number(variant?.id || card.dataset.variantId);
    if (!variantId) return;

    const existing = this.#items.find((item) => item.variantId === variantId);
    if (existing) {
      existing.quantity += 1;
    } else {
      this.#items.push({
        variantId,
        productId: product?.id || Number(card.dataset.productId),
        title: product?.title || card.querySelector('.pack-builder__card-title')?.textContent?.trim() || '',
        variantTitle: this.#visibleVariantTitle(product, variant),
        image: variant?.featured_image || product?.featured_image || card.querySelector('.pack-builder__card-image')?.src || '',
        price: Number(variant?.price ?? card.dataset.price ?? 0),
        quantity: 1,
      });
    }

    card.classList.remove('is-added');
    card.offsetWidth;
    card.classList.add('is-added');

    this.#persist();
    try {
      this.#render();
    } catch (error) {
      console.error('[pack-builder]', error);
    }
  }

  #visibleVariantTitle(product, variant) {
    if (!product || product.has_only_default_variant || !variant) return '';

    const parts = (variant.title || '')
      .split(' / ')
      .filter((part) => part.trim().toLowerCase() !== 'pack' && part.trim().toLowerCase() !== 'default title');

    return parts.join(' / ');
  }

  #removeItem(variantId) {
    const id = Number(variantId);
    this.#items = this.#items.filter((item) => item.variantId !== id);
    this.#persist();
    this.#render();
  }

  #changeQty(variantId, delta) {
    const item = this.#items.find((entry) => entry.variantId === Number(variantId));
    if (!item) return;

    item.quantity += delta;
    if (item.quantity < 1) {
      this.#removeItem(variantId);
      return;
    }

    this.#persist();
    this.#render();
  }

  #selectGift(card) {
    if (!card) return;

    const resolved = this.#resolveVariant(card);
    if (!resolved?.variant?.available) return;

    const { product, variant } = resolved;

    this.#gift = {
      variantId: variant.id,
      productId: product.id,
      title: product.title,
      variantTitle: this.#visibleVariantTitle(product, variant),
      image: variant.featured_image || product.featured_image,
      price: variant.price,
    };

    for (const option of this.#giftOptions) {
      option.classList.toggle('is-selected', option === card);
    }

    this.#persist();
    this.#render();
  }

  #count() {
    return this.#items.reduce((sum, item) => sum + item.quantity, 0);
  }

  #sortedTiers() {
    return [...(this.#config.tiers || [])].filter((tier) => tier.quantity > 0).sort((a, b) => a.quantity - b.quantity);
  }

  #currentTier(count) {
    let current = null;

    for (const tier of this.#sortedTiers()) {
      if (count >= tier.quantity) current = tier;
    }

    return current;
  }

  #nextTier(count) {
    return this.#sortedTiers().find((tier) => count < tier.quantity) || null;
  }

  #subtotal() {
    return this.#items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  }

  #discountAmount(subtotal, percent) {
    if (!percent) return 0;
    return Math.round((subtotal * percent) / 100);
  }

  #render() {
    const count = this.#count();
    const tiers = this.#sortedTiers();
    const current = this.#currentTier(count);
    const next = this.#nextTier(count);
    const maxQty = tiers.at(-1)?.quantity || 1;
    const percent = current?.percent || 0;
    const subtotal = this.#subtotal();
    const discount = this.#discountAmount(subtotal, percent);
    const total = Math.max(0, subtotal - discount);

    this.#renderProgress(count, current, next, maxQty);
    this.#renderRecap(count, subtotal, discount, total, percent);
    this.#renderGift();
    this.#renderCta(count, current);
    this.#applyFilters();
  }

  #renderProgress(count, current, next, maxQty) {
    if (this.#progressFill) {
      const progress = Math.min(100, (count / maxQty) * 100);
      this.#progressFill.style.width = `${progress}%`;
    }

    for (const el of this.#tiers) {
      const qty = Number(el.dataset.quantity);
      el.classList.toggle('is-reached', count >= qty);
    }

    if (current && current.quantity !== this.#lastTierQuantity && count >= current.quantity) {
      const tierEl = this.#tiers.find((el) => Number(el.dataset.quantity) === current.quantity);
      if (tierEl) {
        tierEl.classList.remove('is-just-reached');
        tierEl.offsetWidth;
        tierEl.classList.add('is-just-reached');
      }
    }

    this.#lastTierQuantity = current?.quantity || 0;

    if (!this.#progressPhrase) return;

    const i18n = this.#config.i18n;

    if (count === 0) {
      this.#progressPhrase.textContent = i18n.empty;
      return;
    }

    if (next) {
      const remaining = next.quantity - count;
      this.#progressPhrase.textContent = i18n.nextTier.replace('[count]', String(remaining)).replace('[discount]', this.#discountLabel(next));
      return;
    }

    if (current) {
      this.#progressPhrase.textContent = i18n.progressComplete.replace('[discount]', this.#discountLabel(current));
      return;
    }

    this.#progressPhrase.textContent = '';
  }

  #discountLabel(tier) {
    if (tier.label) return tier.label;
    return `-${tier.percent}%`;
  }

  #renderRecap(count, subtotal, discount, total, percent) {
    if (this.#empty) this.#empty.hidden = count > 0;
    if (this.#recapList) this.#recapList.hidden = count === 0;

    if (this.#countEl) {
      const template = count <= 1 ? this.#config.i18n?.itemCountOne : this.#config.i18n?.itemCountOther;
      this.#countEl.textContent = template ? template.replace('[count]', String(count)) : String(count);
    }

    if (this.#compareEl) {
      this.#compareEl.hidden = discount <= 0;
      this.#compareEl.textContent = formatMoney(subtotal);
    }

    const totalText = formatMoney(total);
    if (this.#totalEl) this.#totalEl.textContent = totalText;
    if (this.#summaryTotal) this.#summaryTotal.textContent = totalText;

    if (this.#saveEl) {
      this.#saveEl.hidden = discount <= 0;
      this.#saveEl.textContent = this.#config.i18n.save.replace('[amount]', formatMoney(discount));
    }

    this.dataset.discountPercent = String(percent);
    this.#renderItems();
  }

  #renderItems() {
    if (!this.#recapList || !this.#itemTemplate) return;

    this.#recapList.replaceChildren();

    for (const item of this.#items) {
      this.#recapList.append(this.#itemNode(item, false));
    }

    if (this.#gift && this.#giftUnlocked()) {
      this.#recapList.append(this.#itemNode(this.#gift, true));
    }
  }

  #itemNode(item, isGift) {
    const node = this.#itemTemplate.content.firstElementChild.cloneNode(true);
    const image = node.querySelector('[data-ref="item-image"]');
    const title = node.querySelector('[data-ref="item-title"]');
    const variant = node.querySelector('[data-ref="item-variant"]');
    const price = node.querySelector('[data-ref="item-price"]');
    const qty = node.querySelector('[data-ref="item-qty"]');
    const qtyWrap = node.querySelector('[data-ref="item-qty-wrap"]');

    if (image) {
      image.src = item.image || '';
      image.alt = item.title;
      image.hidden = !item.image;
    }

    if (title) title.textContent = item.title;
    if (variant) {
      variant.textContent = item.variantTitle || '';
      variant.hidden = !item.variantTitle;
    }

    if (price) price.textContent = isGift ? this.#config.i18n.free : formatMoney(item.price * (item.quantity || 1));

    for (const button of node.querySelectorAll('[data-variant-id]')) {
      button.dataset.variantId = String(item.variantId);
    }

    if (isGift) {
      node.classList.add('pack-builder__item--gift');
      if (qtyWrap) qtyWrap.hidden = true;
      const remove = node.querySelector('[data-action="remove"]');
      if (remove) remove.hidden = true;
    } else if (qty) {
      qty.textContent = String(item.quantity);
    }

    node.classList.add('is-entering');
    return node;
  }

  #giftUnlocked() {
    const gift = this.#config.gift;
    if (!gift?.enabled) return false;
    return this.#count() >= gift.quantity;
  }

  #renderGift() {
    if (!this.#giftPicker) return;

    const unlocked = this.#giftUnlocked();
    if (!unlocked) {
      this.#giftPicker.hidden = true;

      if (this.#gift) {
        this.#gift = null;
        this.#persist();
      }

      for (const option of this.#giftOptions) option.classList.remove('is-selected');
      return;
    }

    if (this.#giftOptions.length === 1 && !this.#gift) {
      this.#selectGift(this.#giftOptions[0]);
    }

    const choosable = this.#giftOptions.length > 1;
    this.#giftPicker.hidden = !choosable;
    if (this.#giftList) this.#giftList.hidden = !choosable;
  }

  #renderCta(count, current) {
    if (!this.#cta) return;

    const minItems = this.#config.minItems || 1;
    const remaining = Math.max(0, minItems - count);
    const label = this.#cta.querySelector('[data-ref="cta-label"]');
    const disabled = count < minItems;

    this.#cta.disabled = disabled;

    for (const button of this.querySelectorAll('[data-action="add-to-cart"]')) {
      button.disabled = disabled;
    }

    if (!label) return;

    if (remaining > 0) {
      label.textContent = this.#config.i18n.minItems.replace('[count]', String(remaining));
      return;
    }

    label.textContent = this.#config.i18n.addToCart;
    void current;
  }

  #storageKey() {
    return `pack-builder:${this.#config.storageKey}`;
  }

  #persist() {
    try {
      localStorage.setItem(this.#storageKey(), JSON.stringify({ items: this.#items, gift: this.#gift }));
    } catch {
      return;
    }
  }

  #restore() {
    let payload;

    try {
      payload = JSON.parse(localStorage.getItem(this.#storageKey()) || 'null');
    } catch {
      return;
    }

    if (!payload?.items) return;

    const validIds = new Set();
    for (const card of this.#cards) {
      const product = this.#getProductData(card);
      if (product?.variants) {
        product.variants.forEach((variant) => validIds.add(variant.id));
      } else if (card.dataset.variantId) {
        validIds.add(Number(card.dataset.variantId));
      }
    }

    this.#items = payload.items.filter((item) => validIds.has(item.variantId));

    if (payload.gift) {
      const giftIds = new Set();
      for (const card of this.#giftOptions) {
        const product = this.#getProductData(card);
        product?.variants?.forEach((variant) => giftIds.add(variant.id));
      }

      this.#gift = giftIds.has(payload.gift.variantId) ? payload.gift : null;

      if (this.#gift) {
        for (const option of this.#giftOptions) {
          const product = this.#getProductData(option);
          option.classList.toggle('is-selected', product?.variants?.some((variant) => variant.id === this.#gift.variantId));
        }
      }
    }
  }

  #clear() {
    this.#items = [];
    this.#gift = null;
    localStorage.removeItem(this.#storageKey());
    if (this.#fold && !this.#desktopQuery?.matches) this.#fold.open = false;
    this.#render();
  }

  async #addToCart() {
    if (this.#cta.disabled || this.#cta.classList.contains('is-loading')) return;

    const items = this.#items.map((item) => ({
      id: item.variantId,
      quantity: item.quantity,
      properties: {
        _pack_builder: this.#config.storageKey,
      },
    }));

    if (this.#gift && this.#giftUnlocked()) {
      items.push({
        id: this.#gift.variantId,
        quantity: 1,
        properties: {
          _pack_builder: this.#config.storageKey,
          _pack_gift: 'true',
        },
      });
    }

    this.#cta.classList.add('is-loading');

    for (const button of this.querySelectorAll('[data-action="add-to-cart"]')) {
      button.classList.add('is-loading');
      button.disabled = true;
    }

    const sections = ['auto-gifts'];
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) sections.push(cartDrawer.dataset.sectionId);

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items, sections: sections.join(',') }),
      });
      const data = await response.json();

      if (data.status && data.status >= 400) {
        throw new Error(data.description || data.message || this.#config.i18n.error);
      }

      const current = this.#currentTier(this.#count());
      let sectionsHtml = data.sections;

      if (current?.discountCode) {
        sectionsHtml = (await this.#applyDiscount(current.discountCode, sections)) || sectionsHtml;
      }

      const currentItemCount = document.querySelector('cart-icon')?.getCurrentItemCount();
      const quantityAdded = items.reduce((sum, item) => sum + item.quantity, 0);

      document.dispatchEvent(
        new CartUpdatedEvent(
          {},
          {
            itemCount: currentItemCount != null ? currentItemCount + quantityAdded : undefined,
            source: 'pack-builder',
            sections: sectionsHtml,
          },
        ),
      );

      const behavior = document.querySelector('cart-icon')?.addToCartBehavior || 'open_cart';
      if (behavior === 'notification') {
        document.dispatchEvent(
          new CustomEvent('toast:open', {
            detail: { type: 'success', message: Theme.translations.addToCartNotificationMessage },
          }),
        );
      }

      this.#clear();
    } catch (error) {
      document.dispatchEvent(
        new CustomEvent('toast:open', {
          detail: { type: 'error', message: error.message || this.#config.i18n.error },
        }),
      );
    } finally {
      this.#cta.classList.remove('is-loading');

      for (const button of this.querySelectorAll('[data-action="add-to-cart"]')) {
        button.classList.remove('is-loading');
      }

      this.#renderCta(this.#count(), this.#currentTier(this.#count()));
    }
  }

  async #applyDiscount(code, sections) {
    try {
      const response = await fetch(Theme.routes.cart_update_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          discount: code,
          sections: sections.join(','),
        }),
      });
      const data = await response.json();
      const failed = data.discount_codes?.find((entry) => entry.code === code && entry.applicable === false);
      if (failed) return null;
      return data.sections;
    } catch {
      return null;
    }
  }
}

if (!customElements.get('pack-builder')) {
  customElements.define('pack-builder', PackBuilder);
}
